import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  RefreshControl,
  Alert,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Svg, Path } from 'react-native-svg';
import AvatarBlurRing from '@/components/AvatarBlurRing';
import { Skeleton } from '@/components/Skeleton';
import * as Haptics from 'expo-haptics';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import Avatar from '@/components/Avatar';
import ProfessionalDiscoveryCarousel from '@/components/ProfessionalDiscoveryCarousel';
import SponsorAppointmentSummaryCard from '@/components/SponsorAppointmentSummaryCard';
import ActionBottomSheet from '@/components/ActionBottomSheet';
import BillBottomNav from '@/components/BillBottomNav';
import Toast from '@/components/Toast';
import { RecordVideoGlyph } from '@/components/video/lobby/LobbyControlPill';
import RecordIcon from '@/components/icons/RecordIcon';
import {
  getMyAppointments,
  getMyPastAppointments,
  getAppointmentJoinToken,
  cancelAppointment,
  getMatchedSponsors,
  SponsorAppointment,
  Sponsor,
  RecoverableAppointmentCheckout,
  getRecoverableAppointmentCheckouts,
} from '@/services/sponsorService';
import { checkCallPermissions } from '@/hooks/useMicPermission';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';

const CARD_WIDTH = 346;
const CARD_GAP = 8;

/** Session access opens 15 minutes before start and closes 2 hours after. */
const JOIN_WINDOW_OPEN_MS = 15 * 60 * 1000;
const JOIN_WINDOW_CLOSE_MS = 2 * 60 * 60 * 1000;
const APPOINTMENT_CHANGE_WINDOW_CLOSED = 'APPOINTMENT_CHANGE_WINDOW_CLOSED';

function getAppointmentMs(appointment: SponsorAppointment): number | null {
  if (appointment.scheduled_at_utc) {
    const ts = new Date(appointment.scheduled_at_utc).getTime();
    if (!Number.isNaN(ts)) return ts;
  }
  if (appointment.scheduled_date && appointment.scheduled_time) {
    const ts = new Date(`${appointment.scheduled_date}T${appointment.scheduled_time}`).getTime();
    if (!Number.isNaN(ts)) return ts;
  }
  return null;
}

function isAppointmentVisible(appointment: SponsorAppointment, nowMs: number): boolean {
  if (appointment.status === 'canceled') return false;
  const ts = getAppointmentMs(appointment);
  if (ts === null) return false;
  return nowMs <= ts + JOIN_WINDOW_CLOSE_MS;
}

function isAppointmentJoinable(appointment: SponsorAppointment, nowMs: number): boolean {
  if (appointment.status === 'canceled') return false;
  const ts = getAppointmentMs(appointment);
  if (ts === null) return false;
  return nowMs >= ts - JOIN_WINDOW_OPEN_MS && nowMs <= ts + JOIN_WINDOW_CLOSE_MS;
}

function isAppointmentChangeWindowClosedError(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === APPOINTMENT_CHANGE_WINDOW_CLOSED;
}

function compareAppointments(a: SponsorAppointment, b: SponsorAppointment): number {
  const tsA = getAppointmentMs(a) ?? 0;
  const tsB = getAppointmentMs(b) ?? 0;
  return tsA - tsB;
}

const CalendarIcon = () => (
  <Svg width='24' height='24' viewBox='0 0 20 20' fill='none'>
    <Path
      d='M12.6289 3.07715V5.55658'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M6.60938 3.07715V5.55658'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M13.0679 4.22021L6.17256 4.2209C4.14713 4.2209 2.88281 5.34879 2.88281 7.42193V13.663C2.88281 15.7689 4.14713 16.9229 6.17256 16.9229L13.062 16.9222C15.0933 16.9222 16.3516 15.7883 16.3516 13.7145V7.42125C16.3576 5.34811 15.0993 4.22021 13.0679 4.22021Z'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M6.26953 9.53125H6.27786'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M6.26953 12.4097H6.27786'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M9.61328 9.53125H9.62161'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M9.61328 12.4097H9.62161'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M12.9492 9.53125H12.9576'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M12.9492 12.4097H12.9576'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </Svg>
);

const PaymentIcon = () => (
  <Svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <Path d='M3 6.5h14M5 13h4M4 4h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z' stroke='#FFFFFF' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </Svg>
);

const ThreeDotsMenuIcon = () => (
  <Svg width='20' height='20' viewBox='0 0 20 21' fill='none'>
    <Path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.4173 16.138C10.8773 16.138 11.2507 16.5113 11.2507 16.9713C11.2507 17.4313 10.8773 17.8047 10.4173 17.8047C9.95732 17.8047 9.58398 17.4313 9.58398 16.9713C9.58398 16.5113 9.95732 16.138 10.4173 16.138Z'
      stroke='#757C85'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.4173 4.47137C10.8773 4.47137 11.2507 4.84472 11.2507 5.30471C11.2507 5.76471 10.8773 6.13804 10.4173 6.13804C9.95732 6.13804 9.58398 5.76471 9.58398 5.30471C9.58398 4.84472 9.95732 4.47137 10.4173 4.47137Z'
      stroke='#757C85'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.4173 10.3047C10.8773 10.3047 11.2507 10.678 11.2507 11.138C11.2507 11.598 10.8773 11.9714 10.4173 11.9714C9.95732 11.9714 9.58398 11.598 9.58398 11.138C9.58398 10.678 9.95732 10.3047 10.4173 10.3047Z'
      stroke='#757C85'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </Svg>
);

export default function AppointmentsScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const { t, language } = useLanguage();
  const { userProfile, sessionReady } = useAuth();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();

  const [upcomingAppointments, setUpcomingAppointments] = useState<SponsorAppointment[]>([]);
  const [pastAppointments, setPastAppointments] = useState<SponsorAppointment[]>([]);
  const [recoverableCheckouts, setRecoverableCheckouts] = useState<RecoverableAppointmentCheckout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  const [joiningAppointmentId, setJoiningAppointmentId] = useState<string | null>(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<SponsorAppointment | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [discoverySponsors, setDiscoverySponsors] = useState<Sponsor[]>([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const joiningRef = useRef(false);
  const carouselRef = useRef<ScrollView>(null);
  const discoveryRequestedRef = useRef(false);

  const visibleAppointments = useMemo(
    () =>
      upcomingAppointments
        .filter((apt) => isAppointmentVisible(apt, nowTick))
        .sort(compareAppointments),
    [upcomingAppointments, nowTick]
  );

  const hasUpcoming = visibleAppointments.length > 0;
  const hasPast = pastAppointments.length > 0;
  const isEmpty = !hasUpcoming && !hasPast;
  const discoveryInterests = userProfile?.interests?.length
    ? userProfile.interests
    : ['Abstinência', 'Família', 'Ansiedade', 'Trauma', 'Depressão', 'Codependência'];
  const hiddenInterestCount = Math.max(0, discoveryInterests.length - 5);
  const discoveryBadges =
    hiddenInterestCount > 0
      ? [...discoveryInterests.slice(0, 5), `+${hiddenInterestCount}`]
      : discoveryInterests.slice(0, 6);
  const discoveryBadgeRows = [discoveryBadges.slice(0, 3), discoveryBadges.slice(3, 6)].filter(
    (row) => row.length > 0
  );

  const loadDiscoverySponsors = useCallback(async () => {
    if (discoveryRequestedRef.current) return;
    discoveryRequestedRef.current = true;
    setDiscoveryLoading(true);
    setDiscoveryError(false);
    try {
      const matches = await getMatchedSponsors();
      setDiscoverySponsors(matches.filter((sponsor) => sponsor.telemed?.enabled === true));
    } catch (error) {
      console.warn('Unable to load professional discovery:', error);
      setDiscoveryError(true);
    } finally {
      setDiscoveryLoading(false);
    }
  }, []);

  // 1-minute tick for appointment visibility
  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const loadAppointments = useCallback(
    async (isRefresh = false) => {
      if (!isRefresh) setIsLoading(true);
      try {
        const [upcomingResult, pastResult, checkoutResult] = await Promise.allSettled([
          getMyAppointments(),
          getMyPastAppointments(),
          getRecoverableAppointmentCheckouts(),
        ]);

        // Upcoming (primary) — must succeed; show error on failure
        if (upcomingResult.status === 'fulfilled') {
          const nowMs = Date.now();
          const upcomingSorted = upcomingResult.value
            .filter((apt) => isAppointmentVisible(apt, nowMs))
            .sort(compareAppointments);
          setUpcomingAppointments(upcomingSorted);
        } else {
          console.error('❌ Failed to load upcoming appointments:', upcomingResult.reason);
          showToast(t('appointments.loadError'), 'error');
        }

        // Past (best-effort) — 404 until server deploys; fail silently
        if (pastResult.status === 'fulfilled') {
          setPastAppointments(pastResult.value);
        } else {
          console.warn('⚠️ Past appointments not available yet:', pastResult.reason);
          setPastAppointments([]);
        }
        if (checkoutResult.status === 'fulfilled') {
          setRecoverableCheckouts(
            checkoutResult.value.filter((checkout) => new Date(checkout.expiresAt).getTime() > Date.now())
          );
        }
      } catch (error) {
        console.error('Error loading appointments:', error);
        showToast(t('appointments.loadError'), 'error');
      } finally {
        if (!isRefresh) setIsLoading(false);
        setRefreshing(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (!sessionReady) return;
    loadAppointments();
  }, [loadAppointments, sessionReady]);

  // Matching is lazy and only runs after both appointment collections have
  // finished loading and the empty state is confirmed.
  useEffect(() => {
    if (!isLoading && isEmpty) {
      loadDiscoverySponsors();
    }
  }, [isEmpty, isLoading, loadDiscoverySponsors]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadAppointments(true);
  }, [loadAppointments]);

  const handleJoinAppointment = async (appointment: SponsorAppointment) => {
    if (joiningRef.current) return;
    joiningRef.current = true;
    setJoiningAppointmentId(appointment.id);
    try {
      const { channel, token } = await getAppointmentJoinToken(appointment.id);
      const permStatus = await checkCallPermissions();
      router.push({
        pathname: permStatus === 'granted' ? '/video-call/lobby' : '/video-call/permissions',
        params: {
          channel,
          sessionType: 'appointment',
          sessionId: appointment.id,
          linkToken: token,
          mode: 'video',
        },
      });
    } catch (err) {
      console.error('Join button failed:', err);
      showToast(t('sponsors.profile.joinRoomError') || 'Erro ao entrar na sala', 'error');
    } finally {
      joiningRef.current = false;
      setJoiningAppointmentId(null);
    }
  };

  const handleResumeCheckout = (checkout: RecoverableAppointmentCheckout) => {
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) => logClarityEvent('appointment_checkout_resume_tapped', { method: 'card' }))
      .catch(() => {});
    router.push({
      pathname: '/sponsors/payment',
      params: {
        bookingSessionId: checkout.id,
        sponsorId: checkout.sponsor.id,
        sponsorName: checkout.sponsor.name || '',
        sponsorPhotoUrl: checkout.sponsor.photoUrl || '',
        sponsorRole: checkout.sponsor.role || '',
        scheduledDate: checkout.scheduledDate,
        scheduledTime: checkout.scheduledTime.slice(0, 5),
        amountMinor: String(checkout.amountMinor),
        currency: checkout.currency,
      },
    });
  };

  const handleReschedule = () => {
    setContextMenuVisible(false);
    setTimeout(() => {
      if (!selectedAppointment) return;
      router.push({
        pathname: `/sponsors/${selectedAppointment.sponsor_id}/agenda`,
        params: {
          isReschedule: '1',
          appointmentId: selectedAppointment.id,
        },
      });
    }, 300);
  };

  const confirmCancel = async (appointment: SponsorAppointment) => {
    try {
      await cancelAppointment(appointment.id);
      showToast(t('appointments.cancelSuccess'), 'success');
      await loadAppointments(true);
    } catch (err) {
      console.error('Cancel appointment failed:', err);
      showToast(
        isAppointmentChangeWindowClosedError(err)
          ? t('appointments.changeWindowClosed')
          : t('appointments.cancelError'),
        'error'
      );
    }
  };

  const handleCancel = () => {
    setContextMenuVisible(false);
    if (!selectedAppointment) return;
    const appointment = selectedAppointment;
    Alert.alert(t('appointments.cancelTitle'), t('appointments.cancelMessage'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('appointments.cancelAppointment'),
        style: 'destructive',
        onPress: () => void confirmCancel(appointment),
      },
    ]);
  };

  const handleNewAppointment = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    import('@/utils/clarityEvents')
      .then(({ logUiAction }) =>
        logUiAction({ surface: 'appointments', element: 'find_professionals' })
      )
      .catch(() => {});
    router.push('/sponsors');
  }, [router]);

  const handleDiscoveryPress = useCallback(
    (sponsor: Sponsor) => {
      import('@/utils/clarityEvents')
        .then(({ logUiAction }) =>
          logUiAction({
            surface: 'appointments',
            element: 'professional_discovery_card',
          })
        )
        .catch(() => {});
      router.push(`/sponsors/${sponsor.id}/agenda`);
    },
    [router]
  );

  const handleCarouselScroll = (event: any) => {
    const x = event.nativeEvent.contentOffset.x;
    const index = Math.round(x / (CARD_WIDTH + CARD_GAP));
    setCarouselIndex(index);
  };

  const locale = useMemo(() => {
    if (language === 'pt') return ptBR;
    if (language === 'es') return es;
    return enUS;
  }, [language]);

  const renderUpcomingCard = (appointment: SponsorAppointment, isCarousel: boolean) => {
    const isJoiningThis = joiningAppointmentId === appointment.id;
    const canJoin = isAppointmentJoinable(appointment, nowTick);
    const cardStyle = isCarousel ? styles.carouselCard : styles.singleCard;

    return (
      <View key={appointment.id} style={cardStyle}>
        {/* Sponsor info */}
        <View style={styles.sponsorInfoRow}>
          <View style={styles.sponsorAvatarSquare}>
            <Avatar
              uri={appointment.sponsors?.photo_url ?? null}
              size={40}
              style={{ borderRadius: 12 }}
              borderWidth={0}
            />
          </View>
          <View style={styles.sponsorDetails}>
            <Text style={styles.sponsorNameText}>{appointment.sponsors?.name}</Text>
            <Text style={styles.sponsorRoleText}>
              {appointment.sponsors?.role || t('sponsors.profile.role')}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.menuButton}
            onPress={() => {
              setSelectedAppointment(appointment);
              setContextMenuVisible(true);
            }}
          >
            <ThreeDotsMenuIcon />
          </TouchableOpacity>
        </View>

        {/* Schedule info */}
        <View style={styles.scheduleInfoRow}>
          <CalendarIcon />
          <Text style={styles.scheduleText}>
            {t('sponsors.profile.scheduledFor')}{' '}
            <Text style={styles.scheduleTextBold}>
              {format(parseISO(appointment.scheduled_date), 'dd/MM', { locale })}
            </Text>{' '}
            {t('sponsors.profile.at')}{' '}
            <Text style={styles.scheduleTextBold}>
              {appointment.scheduled_time.substring(0, 5)}
            </Text>
          </Text>
        </View>

        {/* Action buttons */}
        <TouchableOpacity
          style={[
            styles.primaryButton,
            (!canJoin || isJoiningThis) && styles.primaryButtonDisabled,
          ]}
          disabled={!canJoin || isJoiningThis}
          onPress={() => canJoin && handleJoinAppointment(appointment)}
          activeOpacity={0.8}
        >
          <Text style={styles.primaryButtonText}>
            {isJoiningThis
              ? t('sponsors.profile.joinRoomLoading')
              : canJoin
                ? t('sponsors.profile.joinRoom')
                : t('sponsors.profile.joinRoomTooEarly')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderHistoryCard = (appointment: SponsorAppointment) => (
    <SponsorAppointmentSummaryCard
      key={appointment.id}
      sponsorName={appointment.sponsors?.name}
      sponsorRole={appointment.sponsors?.role || t('sponsors.profile.role')}
      sponsorPhotoUrl={appointment.sponsors?.photo_url}
      scheduledForLabel={t('sponsors.profile.scheduledFor')}
      dateLabel={format(parseISO(appointment.scheduled_date), 'dd/MM', { locale })}
      atLabel={t('sponsors.profile.at')}
      timeLabel={appointment.scheduled_time.substring(0, 5)}
    />
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Top Header — matches diary/community pattern */}
      <View style={styles.topHeader}>
        <TouchableOpacity style={styles.avatarButton} onPress={() => router.push('/perfil')}>
          {hasPadrinhoPlus ? (
            <AvatarBlurRing>
              <Avatar
                uri={userProfile?.avatar_url || null}
                size={32}
                showInitials={true}
                initials={userProfile?.full_name || 'U'}
                borderWidth={0}
              />
            </AvatarBlurRing>
          ) : (
            <Avatar
              uri={userProfile?.avatar_url || null}
              size={32}
              showInitials={true}
              initials={userProfile?.full_name || 'U'}
              borderColor='#7E7D75'
              borderWidth={1}
            />
          )}
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('appointments.title')}</Text>
        <TouchableOpacity
          style={styles.notificationButton}
          onPress={() => router.push('/notifications')}
        >
          <View style={styles.notificationIconContainer}>
            <Svg width='25' height='24' viewBox='0 0 25 24' fill='none'>
              <Path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M12.5 17.8476C18.1392 17.8476 20.7481 17.1242 21 14.2205C21 11.3188 19.1812 11.5054 19.1812 7.94511C19.1812 5.16414 16.5452 2 12.5 2C8.45477 2 5.81885 5.16414 5.81885 7.94511C5.81885 11.5054 4 11.3188 4 14.2205C4.25295 17.1352 6.86177 17.8476 12.5 17.8476Z'
                stroke='#151515'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                d='M14.8889 20.8572C13.5247 22.372 11.3967 22.3899 10.0195 20.8572'
                stroke='#151515'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </Svg>
          </View>
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <ScrollView style={styles.scrollView}>
          <View style={styles.skeletonSection}>
            {/* Upcoming label placeholder */}
            <View style={styles.skeletonLabel} />
            {/* Carousel card placeholder */}
            <View style={styles.skeletonCarouselCard} />
          </View>
          <View style={styles.divider} />
          <View style={styles.skeletonSection}>
            {/* History label placeholder */}
            <View style={styles.skeletonLabel} />
            {/* History list rows */}
            <View style={{ gap: 8 }}>
              <View style={styles.skeletonHistoryRow} />
              <View style={styles.skeletonHistoryRow} />
            </View>
          </View>
        </ScrollView>
      ) : isEmpty ? (
        /* No upcoming AND no past → full-page discovery empty state. */
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.discoveryScroll}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          <View style={styles.discoveryEmpty}>
            <View style={styles.discoveryIconBox}>
              <RecordIcon />
            </View>
            <Text style={styles.discoveryTitle}>{t('appointments.discoveryTitle')}</Text>
            <View style={styles.chipsRow}>
              {discoveryBadgeRows.map((row, rowIndex) => (
                <View key={`interest-row-${rowIndex}`} style={styles.chipRow}>
                  {row.map((chip, chipIndex) => {
                    const isOverflowBadge = chip.startsWith('+');
                    return (
                      <View
                        key={`${chip}-${chipIndex}`}
                        style={[styles.chip, isOverflowBadge && styles.overflowChip]}
                        accessibilityLabel={
                          isOverflowBadge ? `${hiddenInterestCount} interesses a mais` : chip
                        }
                      >
                        <Text style={styles.chipText} numberOfLines={1} ellipsizeMode='tail'>
                          {chip}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
            <TouchableOpacity
              style={styles.discoveryPrimaryPill}
              onPress={handleNewAppointment}
              activeOpacity={0.8}
            >
              <Text style={styles.discoveryPrimaryPillText}>
                {t('appointments.findProfessionals')}
              </Text>
            </TouchableOpacity>
            <View style={[styles.discoveryRail, { width: screenWidth }]}>
              {discoveryLoading && (
                <View style={styles.discoverySkeletonRow}>
                  {[0, 1, 2].map((index) => (
                    <Skeleton
                      key={`discovery-skeleton-${index}`}
                      width={120}
                      height={138}
                      borderRadius={24}
                    />
                  ))}
                </View>
              )}
              {!discoveryLoading && !discoveryError && (
                <ProfessionalDiscoveryCarousel
                  sponsors={discoverySponsors}
                  isActive={isFocused && isEmpty}
                  onPress={handleDiscoveryPress}
                />
              )}
              {!discoveryLoading && discoveryError && (
                <Text style={styles.discoveryErrorText}>{t('appointments.discoveryError')}</Text>
              )}
            </View>
          </View>
        </ScrollView>
      ) : (
        <>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          >
            {recoverableCheckouts.length > 0 && (
              <View style={styles.upcomingSection}>
                <Text style={styles.sectionLabel}>{t('appointments.pendingPayment')}</Text>
                {recoverableCheckouts.map((checkout) => (
                  <View key={checkout.id} style={styles.singleCard}>
                    <SponsorAppointmentSummaryCard
                      sponsorName={checkout.sponsor.name}
                      sponsorRole={checkout.sponsor.role}
                      sponsorPhotoUrl={checkout.sponsor.photoUrl}
                      scheduledForLabel={t('sponsors.profile.scheduledFor')}
                      dateLabel={format(parseISO(checkout.scheduledDate), 'dd/MM')}
                      atLabel={t('sponsors.profile.at')}
                      timeLabel={checkout.scheduledTime.slice(0, 5)}
                    />
                    <TouchableOpacity style={styles.primaryButton} onPress={() => handleResumeCheckout(checkout)}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <PaymentIcon />
                        <Text style={styles.primaryButtonText}>{t('appointments.completePayment')}</Text>
                      </View>
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
            {/* Section 1: Upcoming appointments — hidden when there are none */}
            {hasUpcoming && (
              <>
                <View style={styles.upcomingSection}>
                  <Text style={styles.sectionLabel}>{t('appointments.upcoming')}</Text>

                  {visibleAppointments.length === 1 ? (
                    <View style={styles.singleCardContainer}>
                      {renderUpcomingCard(visibleAppointments[0]!, false)}
                    </View>
                  ) : (
                    <View>
                      <ScrollView
                        ref={carouselRef}
                        horizontal
                        pagingEnabled
                        showsHorizontalScrollIndicator={false}
                        style={styles.carousel}
                        contentContainerStyle={styles.carouselContent}
                        snapToInterval={CARD_WIDTH + CARD_GAP}
                        decelerationRate='fast'
                        snapToAlignment='start'
                        onScroll={handleCarouselScroll}
                        scrollEventThrottle={16}
                      >
                        {visibleAppointments.map((appointment) => (
                          <View key={appointment.id} style={styles.carouselItem}>
                            {renderUpcomingCard(appointment, true)}
                          </View>
                        ))}
                      </ScrollView>
                      {/* Pagination dots */}
                      {visibleAppointments.length > 1 && (
                        <View style={styles.paginationDots}>
                          {visibleAppointments.map((_, i) => (
                            <View
                              key={i}
                              style={[styles.dot, i === carouselIndex && styles.dotActive]}
                            />
                          ))}
                        </View>
                      )}
                    </View>
                  )}
                </View>

                {/* Divider */}
                <View style={styles.divider} />
              </>
            )}

            {/* Section 2: History — hidden when no past appointments */}
            {hasPast && (
              <View style={styles.section}>
                <View style={styles.historyHeader}>
                  <View style={styles.historyIconBox}>
                    <RecordVideoGlyph color='#151515' size={16} />
                  </View>
                  <Text style={styles.historyLabel}>{t('appointments.history')}</Text>
                </View>
                <View style={styles.historyList}>{pastAppointments.map(renderHistoryCard)}</View>
              </View>
            )}

            {/* Bottom padding for nav */}
            <View style={{ height: 120 }} />
          </ScrollView>

          {/* Bottom CTA — fixed above navbar */}
          <View style={[styles.bottomCta, { bottom: bottomInset + 56 }]}>
            <TouchableOpacity
              style={styles.bottomCtaButton}
              onPress={handleNewAppointment}
              activeOpacity={0.8}
            >
              <RecordVideoGlyph color='#FFFFFF' size={20} />
              <Text style={styles.bottomCtaButtonText}>{t('appointments.newAppointment')}</Text>
            </TouchableOpacity>
          </View>
        </>
      )}

      {/* Bottom Nav */}
      <BillBottomNav />

      {/* Toast */}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />

      {/* Context menu */}
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          elevation: 999,
          pointerEvents: contextMenuVisible ? 'auto' : 'none',
        }}
      >
        <ActionBottomSheet
          isVisible={contextMenuVisible}
          onClose={() => setContextMenuVisible(false)}
          title={t('appointments.contextTitle')}
        >
          <Text style={styles.actionDisclaimer}>
            {t('appointments.changeWindowDisclaimer')}
          </Text>
          <TouchableOpacity style={styles.actionCard} onPress={handleReschedule}>
            <Svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
              <Path
                d='M5.1411 4.42627H10.3645C12.1899 4.42627 13.3268 5.71529 13.3268 7.53904V12.4606C13.3268 14.2843 12.1899 15.5733 10.3637 15.5733H5.1411C3.31486 15.5733 2.17969 14.2843 2.17969 12.4606V7.53904C2.17969 5.71529 3.32068 4.42627 5.1411 4.42627Z'
                stroke='#151515'
                strokeWidth='1.84615'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                d='M13.3203 8.45459L15.9407 6.30983C16.3649 5.96221 16.9911 6.02459 17.3387 6.44872C17.4842 6.62668 17.5641 6.84956 17.5632 7.07909L17.5541 12.9262C17.5524 13.4751 17.1075 13.9192 16.5586 13.9175C16.3299 13.9175 16.1079 13.8377 15.9308 13.6921L13.3203 11.5482'
                stroke='#151515'
                strokeWidth='1.84615'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M5.83984 10.0016C5.83984 11.0586 6.69641 11.9143 7.75261 11.9143C8.80954 11.9143 9.66615 11.0586 9.66615 10.0016C9.66615 8.94462 8.80954 8.08887 7.75261 8.08887C6.69641 8.08887 5.83984 8.94462 5.83984 10.0016Z'
                stroke='#151515'
                strokeWidth='1.84615'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </Svg>
            <Text style={styles.actionCardText}>{t('appointments.reschedule')}</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.actionCard} onPress={handleCancel}>
            <Svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
              <Path
                d='M3 6h18'
                stroke='#151515'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6'
                stroke='#151515'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                d='M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'
                stroke='#151515'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                d='M10 11v6'
                stroke='#151515'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <Path
                d='M14 11v6'
                stroke='#151515'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </Svg>
            <Text style={styles.actionCardText}>{t('appointments.cancel')}</Text>
          </TouchableOpacity>
        </ActionBottomSheet>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  topHeader: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9F8F3',
    height: 56,
    flexDirection: 'row',
  },
  avatarButton: {
    width: 32,
    height: 32,
    borderRadius: 12,
  },
  headerTitle: {
    fontSize: 14,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    lineHeight: 14,
  },
  notificationButton: {
    width: 32,
    height: 32,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationIconContainer: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  section: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 24,
    gap: 12,
  },
  upcomingSection: {
    paddingTop: 16,
    paddingBottom: 24,
    gap: 12,
  },
  singleCardContainer: {
    paddingHorizontal: 24,
  },
  sectionLabel: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 14,
    paddingHorizontal: 24,
  },
  carousel: {
    flexGrow: 0,
  },
  carouselContent: {
    paddingLeft: 24,
    paddingRight: 8,
    gap: CARD_GAP,
  },
  carouselItem: {
    width: CARD_WIDTH,
  },
  carouselCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    gap: 16,
  },
  singleCard: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    gap: 16,
  },
  historyCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    gap: 16,
  },
  historyList: {
    gap: 8,
  },
  skeletonSection: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 24,
    gap: 12,
  },
  skeletonLabel: {
    width: 120,
    height: 14,
    borderRadius: 4,
    backgroundColor: '#E7E6E0',
  },
  skeletonCarouselCard: {
    width: CARD_WIDTH,
    height: 180,
    borderRadius: 12,
    backgroundColor: '#E7E6E0',
  },
  skeletonHistoryRow: {
    width: '100%',
    height: 80,
    borderRadius: 12,
    backgroundColor: '#E7E6E0',
  },
  sponsorInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  sponsorAvatarSquare: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    overflow: 'hidden',
    width: 40,
    height: 40,
  },
  sponsorDetails: {
    flexDirection: 'column',
    flex: 1,
    gap: 4,
  },
  sponsorNameText: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
  },
  sponsorRoleText: {
    color: '#757C85',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 12,
  },
  menuButton: {
    padding: 4,
  },
  scheduleInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  scheduleText: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    flex: 1,
  },
  scheduleTextBold: {
    fontFamily: 'Inter_700Bold',
    color: '#151515',
  },
  primaryButton: {
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    backgroundColor: '#151515',
    flex: 1,
    height: 48,
    minHeight: 40,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
  primaryButtonDisabled: {
    backgroundColor: '#DAD8CF',
  },
  paginationDots: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    marginTop: 12,
    height: 6,
  },
  dot: {
    backgroundColor: '#C4C2BA',
    borderRadius: 3,
    width: 6,
    height: 6,
  },
  dotActive: {
    backgroundColor: '#151515',
    width: 16,
    height: 6,
  },
  divider: {
    backgroundColor: '#F0EEE4',
    height: 1,
    width: '100%',
  },
  historyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  historyIconBox: {
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  historyLabel: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
  },
  bottomCta: {
    position: 'absolute',
    left: 0,
    right: 0,
    paddingHorizontal: 24,
    paddingBottom: 16,
    backgroundColor: 'transparent',
  },
  bottomCtaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: '#151515',
    height: 56,
  },
  bottomCtaButtonText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
  actionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    gap: 16,
    marginBottom: 8,
  },
  actionDisclaimer: {
    color: '#807E79',
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    fontStyle: 'italic',
    lineHeight: 16,
    marginBottom: 4,
  },
  actionCardText: {
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  inlineEmptyText: {
    color: '#7E7D75',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    paddingVertical: 8,
  },
  discoveryScroll: {
    flexGrow: 1,
  },
  discoveryEmpty: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
  },
  discoveryIconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    alignItems: 'center',
    justifyContent: 'center',
  },
  discoveryTitle: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    maxWidth: 299,
  },
  chipsRow: {
    width: '100%',
    gap: 8,
    maxWidth: 271,
  },
  chipRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  chip: {
    maxWidth: '31%',
    flexShrink: 1,
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
  },
  overflowChip: {
    minWidth: 44,
  },
  chipText: {
    color: '#3D3C38',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16.8,
  },
  discoveryPrimaryPill: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    backgroundColor: '#151515',
    minHeight: 40,
  },
  discoveryPrimaryPillText: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  discoveryRail: {
    width: '100%',
    minHeight: 112,
    justifyContent: 'center',
  },
  discoverySkeletonRow: {
    flexDirection: 'row',
    gap: 8,
  },
  discoveryErrorText: {
    color: '#7E7D75',
    textAlign: 'center',
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    lineHeight: 16.8,
  },
});
